/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import * as Sentry from '@sentry/react';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import Retracked from 'js/app/retracked';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { get as getUser } from 'js/lib/user';

import { FormLabel, InlineNotification, TextField, VisuallyHidden } from '@coursera/cds-core';

import { AllowedFileTypes } from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/lib/constants';
import UploadedFileBlock from 'bundles/assess-common/components/UploadedFileBlock';
import { maxFileSize, s3Bucket, s3UploadDir } from 'bundles/assess-common/constants/FileUploadConfig';
import { allowedFileTypesForInlineFeedback } from 'bundles/assess-common/constants/constants';
import type { FileUploadSubmissionPartType } from 'bundles/assess-common/types/NaptimeSubmission';
import { UppyFileUploader } from 'bundles/fileuploader-uppy';
import type { UppyEventMap, UppyFile } from 'bundles/fileuploader-uppy';
import SupportedFilesForAiGradingNotification from 'bundles/peer/components/notifications/SupportedFilesForAiGradingNotification';
import UnsupportedFileForAiGradingNotification from 'bundles/peer/components/notifications/UnsupportedFileForAiGradingNotification';

import _t from 'i18n!nls/peer';

const styles = {
  marginBottom: css`
    margin-bottom: var(--cds-spacing-150);
  `,
};

type Props = {
  id: string;
  submissionPart: FileUploadSubmissionPartType;
  update: (newSubmissionPart: FileUploadSubmissionPartType) => void;
  disabled: boolean;
  blockSubmit: (shouldBlockSubmit: boolean) => void;
  uploadButtonHtmlAttributes?: React.HTMLAttributes<HTMLButtonElement>;
  onSkipVivaExam: React.Dispatch<React.SetStateAction<boolean>>;
};

type UploadButtonState = {
  uploadProgressMsg: string;
};

type FileUploadEditViewState = {
  title: string;
  caption: string;
};

export class UploadButton extends React.Component<Props, UploadButtonState> {
  static contextTypes = {
    _eventData: PropTypes.object,
  };

  declare context: LegacyContextType<typeof UploadButton.contextTypes>;

  state: UploadButtonState = {
    uploadProgressMsg: '',
  };

  // Method to check if the file type is allowed for Viva Exam and update the state accordingly
  checkAndSetVivaExamStatus = (fileUrl: string | undefined) => {
    const { onSkipVivaExam } = this.props;
    const fileExtension = fileUrl?.substring(fileUrl.lastIndexOf('.'));
    const isAllowedFileType = AllowedFileTypes.includes(fileExtension?.toLowerCase() || '');
    if (onSkipVivaExam) {
      onSkipVivaExam(!isAllowedFileType);
    }
  };

  onUpload: UppyEventMap['upload'] = (uploadId, files) => {
    const fileIDs = files.map((file) => file.id);
    const id = uploadId;
    const { blockSubmit } = this.props;
    const { logEvent } = this;

    /**
     * explicitly override Uppy's DashboardModal role="dialog" to allow external non-dialog
     *    a11y upload notifications
     * note: this is a hacky workaround until we can upgrade Uppy to v2 and verify Uppy's a11y
     *    improvements include upload progress notifications
     */
    const uppyInnerDialog = document.querySelector('.uppy-Dashboard-inner');
    uppyInnerDialog?.setAttribute('role', 'non-dialog');

    this.setState({ uploadProgressMsg: _t('Upload started') });
    logEvent({ trackingName: 'file_upload_start', extraProps: { uploadSessionId: id, fileIds: fileIDs } });
    blockSubmit(true);
  };

  onUploadProgress: UppyEventMap['upload-progress'] = (_file, progress) => {
    const getUploadProgressMsg = (percentage: number) => _t('Uploaded #{percentage}%', { percentage });
    const uploadPercentage = progress.bytesUploaded / (progress.bytesTotal || 1);
    if (uploadPercentage >= 0.75) {
      this.setState({ uploadProgressMsg: getUploadProgressMsg(75) });
    } else if (uploadPercentage >= 0.5) {
      this.setState({ uploadProgressMsg: getUploadProgressMsg(50) });
    } else if (uploadPercentage >= 0.25) {
      this.setState({ uploadProgressMsg: getUploadProgressMsg(25) });
    }
  };

  onComplete: UppyEventMap['complete'] = (result) => {
    if (result.failed?.length) {
      this.onError(result.failed[0]);
    }

    if (result.successful?.length) {
      this.setState({ uploadProgressMsg: _t('Upload completed') });
      const file = result.successful[0];
      const url = file.response?.body?.location;

      if (url) {
        const { submissionPart, update, blockSubmit } = this.props;
        const { logEvent } = this;

        const newSubmissionPart = cloneDeep(submissionPart);
        newSubmissionPart.definition.fileUrl = url || '';
        this.checkAndSetVivaExamStatus(newSubmissionPart.definition.fileUrl);

        logEvent({
          trackingName: 'file_upload_success',
          extraProps: { uploadSessionId: file.meta.uploadSessionId, file, url },
        });
        blockSubmit(false);
        update(newSubmissionPart);
      } else {
        this.onError(result.successful[0], 'XhrUpload url not found');
      }
    }
  };

  onError = (file: UppyFile, error?: string, response?: string) => {
    const { logEvent } = this;

    logEvent({
      trackingName: 'file_upload_error',
      extraProps: { uploadSessionId: file.meta.uploadSessionId, file, error: error || file.error, response },
    });

    Sentry.captureException(error, {
      extra: {
        message: 'Peer Review file upload error',
        userId: getUser().id,
        file,
        response,
      },
    });
  };

  logEvent = ({ trackingName, extraProps }: { trackingName: string; extraProps?: {} }) => {
    const { id: inputId } = this.props;
    const { _eventData } = this.context;
    const userId = getUser().id;

    Retracked.trackComponent(
      _eventData,
      {
        userId,
        inputId,
        client: 'Uppy-S3',
        ...extraProps,
      },
      trackingName,
      'click'
    );
  };

  removeUploadedFile = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    const { submissionPart, update } = this.props;
    if (event.type === 'click' || ('key' in event && event.key === 'Enter')) {
      const newSubmissionPart = cloneDeep(submissionPart);
      newSubmissionPart.definition.fileUrl = '';
      update(newSubmissionPart);
      this.checkAndSetVivaExamStatus(newSubmissionPart.definition.fileUrl);
    }
  };

  componentDidMount() {
    const { submissionPart } = this.props;
    this.checkAndSetVivaExamStatus(submissionPart.definition.fileUrl);
  }

  render() {
    const { submissionPart, disabled, id, uploadButtonHtmlAttributes } = this.props;
    const { uploadProgressMsg } = this.state;
    const { onUpload, onComplete } = this;

    if (submissionPart.typeName !== 'fileUpload') {
      return null;
    }

    const { fileUrl } = submissionPart.definition;
    const filename = fileUrl?.split('/').pop();

    return (
      <div>
        <div>
          {fileUrl ? (
            <UploadedFileBlock
              fileUrl={fileUrl}
              filename={filename}
              disabled={disabled}
              onRemoveUploadedFile={this.removeUploadedFile}
            />
          ) : (
            <div>
              <UppyFileUploader
                {...{
                  id,
                  buttonContent: _t('Add File'),
                  buttonClasses: ['link-button', 'primary', 'c-upload-button'],
                  disabled,
                  maxFileSize,
                  onUpload,
                  onUploadProgress: this.onUploadProgress,
                  onComplete,
                  onError: (error, file, response) => {
                    if (file) {
                      this.onError(file, JSON.stringify(error), JSON.stringify(response));
                    }
                  },
                  s3Bucket,
                  s3UploadDir,
                  buttonHtmlAttributes: uploadButtonHtmlAttributes,
                  useCdsModal: true,
                }}
              />
            </div>
          )}
          <div role="alert" aria-live="polite">
            <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
              {uploadProgressMsg}
            </VisuallyHidden>
          </div>
        </div>
      </div>
    );
  }
}

class FileUploadEditView extends React.Component<
  {
    submissionPart: FileUploadSubmissionPartType;
    update: (newSubmissionPart: FileUploadSubmissionPartType) => void;
    blockSubmit: (shouldBlockSubmit: boolean) => void;
    disabled: boolean;
    isPartReceivingInlineFeedback?: boolean;
    id: string;
    ariaDescribedby?: string;
    onSkipVivaExam: React.Dispatch<React.SetStateAction<boolean>>;
  } & Pick<Props, 'uploadButtonHtmlAttributes'>,
  FileUploadEditViewState
> {
  state: FileUploadEditViewState = {
    title: this.props.submissionPart.definition.title,
    caption: this.props.submissionPart.definition.caption,
  };

  onChangeTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { submissionPart, update } = this.props;
    const newSubmissionPart = cloneDeep(submissionPart);
    newSubmissionPart.definition.title = event.currentTarget.value;
    this.setState({ title: event.currentTarget.value });
    update(newSubmissionPart);
  };

  onChangeCaption = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { submissionPart, update } = this.props;
    const newSubmissionPart = cloneDeep(submissionPart);
    newSubmissionPart.definition.caption = event.currentTarget.value;
    this.setState({ caption: event.currentTarget.value });
    update(newSubmissionPart);
  };

  renderIncompatibleFileTypeWarning() {
    // If high-touch degree features are enabled, warn learners if they upload incompatible file types. FLEX-18905
    const { submissionPart, isPartReceivingInlineFeedback } = this.props;

    const { fileUrl } = submissionPart.definition;
    const isPlagiarismDetectionEnabled = !!submissionPart.definition.plagiarismMetadata;

    if (!fileUrl || (!isPlagiarismDetectionEnabled && !isPartReceivingInlineFeedback)) {
      return null;
    }

    const fileExtension = fileUrl.split('.').pop()?.toLowerCase() || '';

    if (!allowedFileTypesForInlineFeedback.includes(fileExtension)) {
      return (
        <InlineNotification severity="warning" css={styles.marginBottom}>
          <FormattedMessage
            message={_t(
              'Please submit one of the following file formats ({allowedFileTypeList}) to receive in-line feedback and/or pass plagiarism checks.'
            )}
            allowedFileTypeList={allowedFileTypesForInlineFeedback.map((str) => '.' + str).join(', ')}
          />
        </InlineNotification>
      );
    } else {
      return null;
    }
  }

  render() {
    const {
      submissionPart,
      update,
      disabled,
      blockSubmit,
      id,
      uploadButtonHtmlAttributes,
      ariaDescribedby,
      onSkipVivaExam,
    } = this.props;
    const { title, caption } = this.state;

    const { fileUrl } = submissionPart.definition;

    return (
      <div className="rc-FileUploadEditView">
        <div
          aria-describedby={ariaDescribedby}
          role="group"
          aria-labelledby={`upload-file-label-${id}`}
          css={styles.marginBottom}
        >
          <FormLabel id={`upload-file-label-${id}`}>{_t('Upload File')}</FormLabel>
          {this.renderIncompatibleFileTypeWarning()}
          <SupportedFilesForAiGradingNotification filename={fileUrl} />
          <UploadButton
            id={id}
            submissionPart={submissionPart}
            update={update}
            disabled={disabled}
            blockSubmit={blockSubmit}
            uploadButtonHtmlAttributes={uploadButtonHtmlAttributes}
            onSkipVivaExam={onSkipVivaExam}
          />
          <UnsupportedFileForAiGradingNotification fileUrl={fileUrl} />
        </div>
        {fileUrl && (
          <div>
            <div className="c-peer-review-submit-text-field-container" css={styles.marginBottom}>
              <TextField
                label={_t('Title')}
                value={title}
                onChange={this.onChangeTitle}
                aria-label={_t('Enter an optional title for the uploaded file')}
                id={`text-input-title-${id}`}
                fullWidth
              />
            </div>
            <div className="c-peer-review-submit-text-field-container">
              <TextField
                label={_t('Caption')}
                value={caption}
                onChange={this.onChangeCaption}
                id={`text-input-caption-${id}`}
                multiline
                fullWidth
                maxRows={3}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default FileUploadEditView;
